import axios from "axios";
import { safeLS } from "@/lib/safeStorage";
import { esHostPortal } from "@/lib/portal";

// En los dominios del portal (tierrascore.com, …) la API va al mismo origen: sin CORS ni cookies entre sitios.
const BACKEND_URL = esHostPortal() && window.location.protocol === "https:" ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach bearer token from localStorage as fallback to cookies
api.interceptors.request.use((config) => {
  const token = safeLS.getItem("tm_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Ocurrió un error. Intente nuevamente.";
  // 520/522/524 del proxy (servidor ocupado esperando al SNC): mensaje claro en vez del texto técnico en inglés
  if (typeof detail === "string" && /cloudflare|origin web server/i.test(detail)) return "El servidor está ocupado esperando al catastro (SNC) · reintentá en un minuto.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Formato Paraguay (es-PY): punto de miles, coma decimal. Valor SOLO (sin $ ni "USD";
// el "USD" se muestra una vez en el título de la tarjeta/columna).
const _nfPY = (min, max) =>
  new Intl.NumberFormat("es-PY", { minimumFractionDigits: min, maximumFractionDigits: max });

export const fmtUSD = (n) => _nfPY(2, 2).format(Number(n || 0));

export const fmtNum = (n) => _nfPY(0, 0).format(Number(n || 0));

// Para tablas: 0 → "—"; |n|≥1000 sin decimales; si no, 2 decimales.
export const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (!v) return "—";
  return Math.abs(v) >= 1000 ? _nfPY(0, 0).format(v) : _nfPY(2, 2).format(v);
};

// Compacto para tarjetas angostas (USD 5,9 M). Valor completo en title.
export const fmtUSDc = (n) => {
  const v = Number(n || 0);
  const a = Math.abs(v);
  if (a >= 1e6) return _nfPY(0, 1).format(v / 1e6) + " M";
  if (a >= 1e3) return _nfPY(0, 1).format(v / 1e3) + " K";
  return _nfPY(0, 0).format(v);
};

export const fmtDate = (iso) => {
  if (!iso) return "";
  const p = String(iso).split("-");
  if (p.length === 3 && p[0].length === 4) return `${p[2].slice(0, 2)}/${p[1]}/${p[0]}`;
  return String(iso);
};

// ---- Abreviación de nombres (solo visual; exportaciones usan el nombre completo) ----
const _cap = (w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : "");

// Vendedor → "Apellido I." (ver reglas con/sin coma)
export const abbrevVendedor = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  let apellido = "", inicial = "";
  if (s.includes(",")) {
    const [ape, nom] = s.split(",");
    apellido = (ape.trim().split(/\s+/)[0] || "");
    inicial = ((nom || "").trim().split(/\s+/)[0] || "")[0] || "";
  } else {
    const parts = s.split(/\s+/);
    inicial = (parts[0] || "")[0] || "";
    apellido = parts[1] || parts[0] || "";
  }
  return `${_cap(apellido)} ${inicial.toUpperCase()}.`.trim();
};

const _surnameOf = (raw) => {
  const s = String(raw || "").trim();
  return s.includes(",") ? (s.split(",")[0].trim().split(/\s+/)[0] || "") : (s.trim().split(/\s+/)[1] || s);
};

// Mapa fullName → abreviatura, resolviendo colisiones (segunda letra del apellido)
export const buildVendedorAbbrev = (names) => {
  const base = {};
  (names || []).forEach((n) => { if (n) base[n] = abbrevVendedor(n); });
  const counts = {};
  Object.values(base).forEach((a) => { counts[a] = (counts[a] || 0) + 1; });
  const out = {};
  Object.entries(base).forEach(([full, ab]) => {
    if (counts[ab] > 1) {
      const extra = (_surnameOf(full) || "").slice(1, 2).toUpperCase();
      out[full] = extra ? ab.replace(/\s(\w)\.$/, ` $1${extra}.`) : ab;
    } else out[full] = ab;
  });
  return out;
};

// Cliente → título, con abreviaturas de razón social
export const fmtCliente = (raw) => {
  if (!raw) return "";
  let s = " " + String(raw).trim().toUpperCase() + " ";
  s = s.replace(/ SOCIEDAD DE RESPONSABILIDAD LIMITADA /g, " SRL_ ")
       .replace(/ SOCIEDAD AN[OÓ]NIMA /g, " SA_ ")
       .replace(/ COOPERATIVA DE PRODUCCI[OÓ]N /g, " COOPPROD_ ")
       .replace(/ COOPERATIVA /g, " COOP_ ")
       .replace(/ AGROPECUARIA /g, " AGROP_ ")
       .replace(/ AGR[IÍ]COLA /g, " AGRIC_ ")
       .replace(/ COMERCIO /g, " COM_ ")
       .replace(/ LIMITADA /g, " LTDA_ ")
       .replace(/ S\.?A\.? /g, " SA_ ")
       .replace(/ S\.?R\.?L\.? /g, " SRL_ ");
  let t = s.trim().toLowerCase().replace(/(^|[\s.(\-/])([a-záéíóúñ])/g, (m, p, c) => p + c.toUpperCase());
  const map = { Coopprod_: "Coop. de Prod.", Coop_: "Coop.", Agrop_: "Agrop.", Agric_: "Agríc.", Com_: "Com.", Ltda_: "Ltda.", Sa_: "S.A.", Srl_: "S.R.L." };
  t = t.replace(/Coopprod_|Coop_|Agrop_|Agric_|Com_|Ltda_|Sa_|Srl_/g, (m) => map[m] || m);
  return t;
};
